import { keyframes } from "@emotion/react";
import { Box } from "@mui/joy";
import { useEffect, useRef, useState, type ReactNode } from "react";

interface MarqueeProps {
  children: ReactNode;
  speed?: number; // Duration in seconds for one complete cycle
  pauseOnHover?: boolean;
}

const marqueeAnimation = keyframes`
  0% {
    transform: translateX(0px);
  }
  25% {
    transform: translateX(0px);
  }
  45% {
    transform: translateX(var(--translate-distance));
  }
  65% {
    transform: translateX(var(--translate-distance));
  }
  85% {
    transform: translateX(0px);
  }
  100% {
    transform: translateX(0px);
  }
`;

export function Marquee({
  children,
  speed = 8,
  pauseOnHover = false,
}: MarqueeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [translateDistance, setTranslateDistance] = useState(0);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && contentRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const contentWidth = contentRef.current.scrollWidth;

        if (contentWidth > containerWidth) {
          // Calculate exact distance needed to show the full content
          const distance = contentWidth - containerWidth;

          setTranslateDistance(-distance);
          setShouldAnimate(true);
        } else {
          setShouldAnimate(false);
          setTranslateDistance(0);
        }
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [children]);

  return (
    <Box
      ref={containerRef}
      sx={{
        overflow: "hidden",
        width: "100%",
        "--translate-distance": `${translateDistance}px`,
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          display: "inline-block",
          whiteSpace: "nowrap",
          animation: shouldAnimate
            ? `${marqueeAnimation} ${speed}s linear infinite`
            : "none",
          animationPlayState: pauseOnHover ? "running" : undefined,
          "&:hover":
            pauseOnHover && shouldAnimate
              ? {
                  animationPlayState: "paused",
                }
              : {},
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
